import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { authApi } from "@/api";

export interface UserInfo {
  id: number;
  username: string;
  name: string;
  /** 角色标识，常见：super_admin / admin / dept_manager / designer …（users 表旧字段，可能与 RBAC 不一致） */
  role?: string;
  /** 部分接口用 role_code 与 role 二选一 */
  role_code?: string;
  /** 登录接口返回的 RBAC 角色 code 列表，与 role 同时存在时优先据此解析 */
  roles?: string[];
  /** 是否为组长（users.is_team_leader + team_id，或 roles 含 team_leader） */
  isTeamLeader?: boolean;
  is_team_leader?: boolean;
  phone: string | null;
  email: string | null;
  status: string;
}

/** 统一成 snake_case 小写，便于与后端约定比对 */
function normalizeRoleKey(raw: string): string {
  const normalized = raw
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  // 兼容历史/简写角色标识
  if (normalized === "auto" || normalized === "automation") {
    return "automation_delivery";
  }
  return normalized;
}

/** 与 RBAC 多角色、旧 users.role 并存时，按权限从高到低取第一个用于前端判断 */
const ROLE_PRECEDENCE = [
  "super_admin",
  "admin",
  "dept_manager",
  "automation_delivery",
  "team_leader",
  "autocut",
  "designer",
  "social_media_ops",
  "operator",
  "viewer",
] as const;

const DEPT_MANAGER_LIKE_ROLE_CODES = [
  "super_admin",
  "admin",
  "dept_manager",
  "automation_delivery",
] as const;

const DUAL_D0_ROI_ROLE_CODES = [
  "super_admin",
  "admin",
  "dept_manager",
  "analyst",
  "data_analyst",
] as const;

/** 超级管理员 / 管理员 / 部门经理 / 自动投放（与素材、作品、主页等经理级能力一致） */
export function isAdminSuperOrDeptManager(
  u: UserInfo | Record<string, unknown> | null | undefined,
): boolean {
  return DEPT_MANAGER_LIKE_ROLE_CODES.includes(
    getEffectiveRoleCode(u) as (typeof DEPT_MANAGER_LIKE_ROLE_CODES)[number],
  );
}

export function isSocialMediaOpsUser(
  u: UserInfo | Record<string, unknown> | null | undefined,
): boolean {
  return collectNormalizedRoleCodes(
    (u ?? {}) as Record<string, unknown>,
  ).includes("social_media_ops");
}

/** 自动剪辑角色（设计仪表盘查看 AI 设计师数据） */
export function isAutoCutUser(
  u: UserInfo | Record<string, unknown> | null | undefined,
): boolean {
  return collectNormalizedRoleCodes(
    (u ?? {}) as Record<string, unknown>,
  ).includes("autocut");
}

/** 自动投放角色（含与管理员叠加时仍可识别） */
export function isAutomationDeliveryUser(
  u: UserInfo | Record<string, unknown> | null | undefined,
): boolean {
  return collectNormalizedRoleCodes(
    (u ?? {}) as Record<string, unknown>,
  ).includes("automation_delivery");
}

/** 移动端可同时查看 D0 ROI（激活）和 D0 ROI 的角色。 */
export function canViewBothD0RoiMetrics(
  u: UserInfo | Record<string, unknown> | null | undefined,
): boolean {
  return collectNormalizedRoleCodes((u ?? {}) as Record<string, unknown>).some(
    (code) =>
      DUAL_D0_ROI_ROLE_CODES.includes(
        code as (typeof DUAL_D0_ROI_ROLE_CODES)[number],
      ),
  );
}

/** 可将推广对象「复制为自动化模板」：管理员 / 自动投放 */
export function canCopyAsAutomationTemplate(
  u: UserInfo | Record<string, unknown> | null | undefined,
): boolean {
  const codes = collectNormalizedRoleCodes(
    (u ?? {}) as Record<string, unknown>,
  );
  return codes.some(
    (c) => c === "super_admin" || c === "admin" || c === "automation_delivery",
  );
}

/** 素材列表「花费」列：超管、管理员、部门经理、组长、自动投放 */
export function canViewCreativeSpend(
  u: UserInfo | Record<string, unknown> | null | undefined,
): boolean {
  const o = (u ?? {}) as Record<string, unknown>;
  const codes = collectNormalizedRoleCodes(o);
  if (
    codes.some((c) =>
      ["super_admin", "admin", "dept_manager", "automation_delivery"].includes(
        c,
      ),
    )
  ) {
    return true;
  }
  if (o.is_dept_manager === true || o.is_dept_manager === 1) {
    return true;
  }

  return isTeamLeaderUser(u);
}

function collectNormalizedRoleCodes(o: Record<string, unknown>): string[] {
  const out: string[] = [];
  const add = (raw: unknown) => {
    if (typeof raw === "string" && raw !== "") {
      const n = normalizeRoleKey(raw);
      if (n && !out.includes(n)) out.push(n);
    }
  };
  if (Array.isArray(o.roles)) {
    for (const x of o.roles) add(x);
  }
  const pick = (v: unknown): string =>
    typeof v === "string" && v !== "" ? v : "";
  add(
    pick(o.role) || pick(o.role_code) || pick(o.roleKey) || pick(o.role_slug),
  );
  return out;
}

/**
 * 从登录/缓存对象中取出「有效」角色（兼容 roles[]、role、role_code、嵌套 user）
 */
export function getEffectiveRoleCode(
  u: UserInfo | Record<string, unknown> | null | undefined,
): string {
  if (!u || typeof u !== "object") return "";
  const o = u as Record<string, unknown>;
  let codes = collectNormalizedRoleCodes(o);
  if (!codes.length && o.user && typeof o.user === "object") {
    codes = collectNormalizedRoleCodes(o.user as Record<string, unknown>);
  }
  for (const key of ROLE_PRECEDENCE) {
    if (codes.includes(key)) return key;
  }
  return codes[0] ?? "";
}

/** 账号是否带有设计师角色（与 isDesignerOnly 不同：不排斥组长等叠加角色） */
export function hasDesignerRole(
  u: UserInfo | Record<string, unknown> | null | undefined,
): boolean {
  return collectNormalizedRoleCodes(
    (u ?? {}) as Record<string, unknown>,
  ).includes("designer");
}

/** 是否为组长（接口字段或 RBAC team_leader 角色） */
export function isTeamLeaderUser(
  u: UserInfo | Record<string, unknown> | null | undefined,
): boolean {
  if (!u || typeof u !== "object") return false;
  const o = u as Record<string, unknown>;
  if (
    o.isTeamLeader === true ||
    o.is_team_leader === true ||
    o.is_team_leader === 1
  ) {
    return true;
  }
  return getEffectiveRoleCode(u) === "team_leader";
}

function mapApiUserToUserInfo(
  payload: Record<string, unknown>,
  existing: UserInfo | null,
): UserInfo {
  const str = (v: unknown): string | undefined =>
    typeof v === "string" && v.trim() !== "" ? v.trim() : undefined;
  const id = Number(payload.id ?? existing?.id ?? 0);
  const username = str(payload.username) ?? existing?.username ?? "";
  const name =
    str(payload.name) ??
    str(payload.real_name) ??
    str(payload.realName) ??
    existing?.name ??
    username;
  const role = str(payload.role) ?? str(payload.role_code) ?? existing?.role;
  let roles: string[] | undefined;
  if (Array.isArray(payload.roles)) {
    const parsed = (payload.roles as unknown[]).filter(
      (x): x is string => typeof x === "string" && x !== "",
    );
    roles = parsed.length > 0 ? parsed : undefined;
  } else if (role) {
    roles = [normalizeRoleKey(role)];
  } else {
    roles = existing?.roles;
  }
  const isTeamLeader =
    payload.isTeamLeader === true ||
    payload.is_team_leader === true ||
    payload.is_team_leader === 1;

  return {
    id,
    username,
    name,
    role,
    roles: roles?.length ? roles : undefined,
    isTeamLeader: isTeamLeader || undefined,
    is_team_leader: isTeamLeader || undefined,
    phone: str(payload.phone) ?? existing?.phone ?? null,
    email: str(payload.email) ?? existing?.email ?? null,
    status: str(payload.status) ?? existing?.status ?? "enabled",
  };
}

export const useUserStore = defineStore("user", () => {
  // 从localStorage恢复用户信息
  const savedUser = localStorage.getItem("user");
  const userInfo = ref<UserInfo | null>(
    savedUser ? JSON.parse(savedUser) : null,
  );
  const token = ref<string | null>(localStorage.getItem("token"));

  const isLoggedIn = computed(() => !!token.value);

  const isSuperAdmin = computed(
    () => getEffectiveRoleCode(userInfo.value) === "super_admin",
  );
  const isAdmin = computed(() => {
    const r = getEffectiveRoleCode(userInfo.value);
    return r === "super_admin" || r === "admin";
  });
  /** 可管理素材「创建广告是否可选」开关：超管、管理员、部门经理 */
  const canManageCreativeAdSelection = computed(() =>
    isAdminSuperOrDeptManager(userInfo.value),
  );

  /** 可打开素材合规预审页：经理级 + 组长 + 设计师 + 自动投放 */
  const canAccessComplianceReview = computed(
    () =>
      isAdminSuperOrDeptManager(userInfo.value) ||
      isTeamLeaderUser(userInfo.value) ||
      hasDesignerRole(userInfo.value) ||
      isAutomationDeliveryUser(userInfo.value),
  );

  /** 可确认允许/不允许投放：经理级 + 组长 + 设计师 + 自动投放 */
  const canDecideCompliancePlacement = computed(
    () =>
      isAdminSuperOrDeptManager(userInfo.value) ||
      isTeamLeaderUser(userInfo.value) ||
      hasDesignerRole(userInfo.value) ||
      isAutomationDeliveryUser(userInfo.value),
  );

  /** 作品列表「AI 剪辑」：超管、管理员、部门经理 */
  const canUseWorksAiClip = computed(() =>
    isAdminSuperOrDeptManager(userInfo.value),
  );

  /** 作品列表「标记已测」：超管、管理员、部门经理 */
  const canMarkDramaTesting = computed(() =>
    isAdminSuperOrDeptManager(userInfo.value),
  );

  /** 主页同步、分配用户：超管、管理员、部门经理 */
  const isAdminOrDeptManager = computed(() =>
    isAdminSuperOrDeptManager(userInfo.value),
  );

  /** 素材页「批量自动AI广告」：超管、管理员、组长 */
  const canUseBatchAiAutomation = computed(
    () => isAdmin.value || isTeamLeaderUser(userInfo.value),
  );

  const canViewCreativeSpendAccess = computed(() =>
    canViewCreativeSpend(userInfo.value),
  );

  const isTeamLeader = computed(() => isTeamLeaderUser(userInfo.value));

  /**
   * 「设计师独占作用域」：role=designer，且不是 super_admin/admin/dept_manager/analyst。
   * 这类用户在报表里只能看到自己作为 designer 的数据（后端也会强制这一点）。
   */
  const isDesignerOnly = computed(() => {
    const codes = collectNormalizedRoleCodes(
      (userInfo.value ?? {}) as Record<string, unknown>,
    );
    const override = [
      "super_admin",
      "admin",
      "dept_manager",
      "automation_delivery",
      "analyst",
    ];
    if (codes.some((c) => override.includes(c))) {
      return false;
    }
    const o = userInfo.value as Record<string, unknown> | null;
    if (
      o?.isTeamLeader === true ||
      o?.is_team_leader === true ||
      o?.is_team_leader === 1
    ) {
      return false;
    }
    return getEffectiveRoleCode(userInfo.value) === "designer";
  });

  /** 社媒运营角色：推广链接 + 投放作品报表等限定能力 */
  const isSocialMediaOps = computed(() => isSocialMediaOpsUser(userInfo.value));

  /** 自动投放角色 */
  const isAutomationDelivery = computed(() =>
    isAutomationDeliveryUser(userInfo.value),
  );

  /** 移动端主页及报表：同时展示 D0 ROI（激活）和 D0 ROI。 */
  const canViewBothD0Roi = computed(() =>
    canViewBothD0RoiMetrics(userInfo.value),
  );

  /** 推广页「复制为自动化模板」：管理员 + 自动投放 */
  const canCopyAsAutomationTemplateRole = computed(() =>
    canCopyAsAutomationTemplate(userInfo.value),
  );

  let refreshPromise: Promise<boolean> | null = null;
  let lastRefreshAt = 0;
  const REFRESH_MIN_INTERVAL_MS = 15_000;

  /** 从服务端拉取最新角色/资料，更新后各页管理员/经理按钮会随之变化 */
  async function refreshUserInfo(force = false): Promise<boolean> {
    if (!token.value) {
      return false;
    }
    const now = Date.now();
    if (!force && now - lastRefreshAt < REFRESH_MIN_INTERVAL_MS) {
      return true;
    }
    if (refreshPromise) {
      return refreshPromise;
    }
    refreshPromise = (async () => {
      try {
        const data = (await authApi.getUserInfo()) as Record<string, unknown>;
        setUserInfo(mapApiUserToUserInfo(data, userInfo.value));
        lastRefreshAt = Date.now();
        return true;
      } catch {
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
    return refreshPromise;
  }

  function setUserInfo(info: UserInfo) {
    userInfo.value = info;
    localStorage.setItem("user", JSON.stringify(info));
  }

  function setToken(newToken: string) {
    token.value = newToken;
    localStorage.setItem("token", newToken);
  }

  function logout() {
    userInfo.value = null;
    token.value = null;
    lastRefreshAt = 0;
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  }

  return {
    userInfo,
    token,
    isLoggedIn,
    isSuperAdmin,
    isAdmin,
    isAdminOrDeptManager,
    isTeamLeader,
    canUseBatchAiAutomation,
    canViewCreativeSpend: canViewCreativeSpendAccess,
    isDesignerOnly,
    isSocialMediaOps,
    isAutomationDelivery,
    canViewBothD0Roi,
    canCopyAsAutomationTemplateRole,
    canManageCreativeAdSelection,
    canAccessComplianceReview,
    canDecideCompliancePlacement,
    canUseWorksAiClip,
    canMarkDramaTesting,
    setUserInfo,
    refreshUserInfo,
    setToken,
    logout,
  };
});
